// Shared helpers for the long-form `{ date, group, value }` rows the analytics APIs return.

interface PivotSeries {
  key: string;
  name: string;
}

export interface PivotRow {
  date: string;
  [key: string]: string | number;
}

interface PivotResult {
  data: PivotRow[];
  series: PivotSeries[];
}

const compareX = (a: string, b: string): number => a.localeCompare(b, undefined, { numeric: true });

/**
 * Pivots long-form rows into one row per x (`[{ date, <group>: value, ... }]`, sorted by x)
 * plus the series to draw, in order of first appearance. A missing group/x combination is
 * filled with 0 (stacked bars); with `gaps: 'omit'` it is left out so line and area charts
 * connect across the gap.
 */
export const pivotSeries = <R extends { group: string; group_name?: string }>(
  rows: R[],
  x: (r: R) => string,
  value: (r: R) => number,
  gaps: 'zero' | 'omit' = 'zero',
): PivotResult => {
  const seriesByKey = new Map<string, PivotSeries>();
  const dataByX = new Map<string, PivotRow>();

  for (const row of rows) {
    if (!seriesByKey.has(row.group)) {
      seriesByKey.set(row.group, { key: row.group, name: row.group_name ?? row.group });
    }
    const xValue = x(row);
    const entry: PivotRow = dataByX.get(xValue) ?? { date: xValue };
    entry[row.group] = value(row);
    dataByX.set(xValue, entry);
  }

  const series = [...seriesByKey.values()];
  const data = [...dataByX.values()].sort((a, b) => compareX(a.date, b.date));
  if (gaps === 'zero') {
    for (const entry of data) {
      for (const s of series) {
        if (!(s.key in entry)) entry[s.key] = 0;
      }
    }
  }
  return { data, series };
};

/** The rows of one chart scope: the `overall` group, or every program's group. */
export const scopeRows = <R extends { group: string }>(
  rows: R[],
  scope: 'overall' | 'by_program',
): R[] => rows.filter(r => (scope === 'overall') === (r.group === 'overall'));

export interface DonutSlice {
  key: string;
  name: string;
  value: number;
}

export const OTHER_SLICE_KEY = 'other';

/**
 * Keeps the `n` largest slices (count descending, ties by name) and sums the rest into one
 * trailing `Other` slice. Nothing is added when everything fits.
 */
export const foldTopN = (slices: DonutSlice[], n: number): DonutSlice[] => {
  const sorted = [...slices].sort(
    (a, b) => b.value - a.value || a.name.localeCompare(b.name) || a.key.localeCompare(b.key),
  );
  if (sorted.length <= n) return sorted;
  const rest = sorted.slice(n).reduce((sum, s) => sum + s.value, 0);
  return [...sorted.slice(0, n), { key: OTHER_SLICE_KEY, name: 'Other', value: rest }];
};

/** `foldTopN` over API breakdown entries (`group`, `group_name`, `case_count`). */
export const topNWithOther = <
  E extends { group: string; group_name?: string; case_count?: number },
>(
  entries: E[],
  n = 7,
): DonutSlice[] =>
  foldTopN(
    entries.map(e => ({ key: e.group, name: e.group_name ?? e.group, value: e.case_count ?? 0 })),
    n,
  );

interface StackRow {
  x: string;
  group: string;
  group_name?: string;
  value: number;
}

/**
 * Folds long-form stacked rows past the `n` largest groups (by summed value, ties by name) into
 * one trailing `Other` group, merging the folded rows per x. Rows come back regrouped largest
 * series first with `Other` last, so series and legend order follow the fold.
 */
export const foldRowsTopN = (rows: StackRow[], n: number): StackRow[] => {
  const totals = new Map<string, DonutSlice>();
  for (const r of rows) {
    const t = totals.get(r.group) ?? { key: r.group, name: r.group_name ?? r.group, value: 0 };
    t.value += r.value;
    totals.set(r.group, t);
  }
  const slices = foldTopN([...totals.values()], n);
  const kept = new Set(slices.filter(s => s.key !== OTHER_SLICE_KEY).map(s => s.key));

  const rowsByGroup = new Map<string, StackRow[]>();
  const otherByX = new Map<string, StackRow>();
  for (const r of rows) {
    if (kept.has(r.group)) {
      const list = rowsByGroup.get(r.group) ?? [];
      list.push(r);
      rowsByGroup.set(r.group, list);
    } else {
      const merged = otherByX.get(r.x) ?? {
        x: r.x,
        group: OTHER_SLICE_KEY,
        group_name: 'Other',
        value: 0,
      };
      merged.value += r.value;
      otherByX.set(r.x, merged);
    }
  }
  return slices.flatMap(s =>
    s.key === OTHER_SLICE_KEY ? [...otherByX.values()] : (rowsByGroup.get(s.key) ?? []),
  );
};
